/**
 * Admin platform-cost dashboard (#494 A8): one chart + a 2×2 block of 4 EUR tiles.
 *
 * Self-contained — fetches its own data from the admin cost endpoints (A6) and
 * renders below the KPI charts on AdminPage. All amounts are EUR via the shared
 * `formatEur` helper; date ticks use the same de-DE short format as KPICharts.
 * Tiles fall back to €0,00 and the chart to "Noch keine Daten" when there is no
 * (or only partial) data, so a 404/empty payload never crashes the page.
 *
 * HOF-194 retired the `Kosten je Quelle (EUR)` per-source chart — ~25 AWS
 * sources in a 180 px plot, every series but one flat on the axis floor and a
 * legend taller than the plot — and moved the four tiles into the half-width
 * slot it vacated, so they cost no vertical band of their own. `by_source` is
 * still returned by `GET /api/admin/cost/series`; nothing here reads it.
 */
import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { formatEur } from '../../utils/formatEur';
import { taggedFetch } from '../../services/taggedFetch';
import './CostDashboard.css';

interface CostSourceAmount {
  source: string;
  cost_eur: number;
}

interface CostSummary {
  last_month_total_eur: number;
  this_month_to_date_eur: number;
  this_month_projected_eur: number;
  per_source: CostSourceAmount[];
}

interface SeriesPoint {
  date: string;
  cost_eur: number;
}

interface CostSeries {
  daily: SeriesPoint[];
}

const formatDateTick = (value: string) =>
  new Date(value).toLocaleDateString('de-DE', { month: 'short', day: 'numeric' });

const eurTooltip = (v: number, name: string): [string, string] => [formatEur(v), name];
const dateLabel = (v: string | number) => new Date(v as string).toLocaleDateString('de-DE');

export const CostDashboard: React.FC = () => {
  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [series, setSeries] = useState<CostSeries | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [sumRes, serRes] = await Promise.all([
          taggedFetch('/api/admin/cost/summary', { credentials: 'include' }),
          taggedFetch('/api/admin/cost/series?days=30', { credentials: 'include' }),
        ]);
        if (!cancelled && sumRes.ok) setSummary(await sumRes.json());
        if (!cancelled && serRes.ok) setSeries(await serRes.json());
      } catch (err) {
        console.error('Failed to load cost dashboard:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const daily = series?.daily ?? [];

  const lastMonth = summary?.last_month_total_eur ?? 0;
  const mtd = summary?.this_month_to_date_eur ?? 0;
  const projected = summary?.this_month_projected_eur ?? 0;
  const topSource = summary?.per_source?.[0] ?? null;

  if (loading) {
    return (
      <div className="cost-dashboard">
        <div className="kpi-no-data">Lädt…</div>
      </div>
    );
  }

  return (
    <div className="cost-dashboard">
      <div className="kpi-charts-row">
        <div className="kpi-chart-card">
          <div className="kpi-chart-title">Kosten gesamt (EUR)</div>
          {daily.length === 0 ? (
            <div className="kpi-no-data">Noch keine Daten</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={daily} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tickFormatter={formatDateTick} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={dateLabel} formatter={(v: number) => eurTooltip(v, 'Kosten')} />
                <Line type="monotone" dataKey="cost_eur" stroke="#2563eb" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="cost-tiles-row">
          <div className="cost-tile">
            <div className="kpi-chart-title">Letzter Monat</div>
            <div className="kpi-latest-value">{formatEur(lastMonth)}</div>
          </div>
          <div className="cost-tile">
            <div className="kpi-chart-title">Diesen Monat</div>
            <div className="kpi-latest-value">{formatEur(mtd)}</div>
          </div>
          <div className="cost-tile">
            <div className="kpi-chart-title">Prognose</div>
            <div className="kpi-latest-value">{formatEur(projected)}</div>
          </div>
          <div className="cost-tile">
            <div className="kpi-chart-title">Top-Quelle</div>
            {topSource ? (
              <>
                <div className="cost-tile-source">{topSource.source}</div>
                <div className="kpi-latest-value">{formatEur(topSource.cost_eur)}</div>
              </>
            ) : (
              <div className="kpi-latest-value">—</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostDashboard;
